/**
 * Panel access context — view/write helpers for the current org admin user.
 */

import React, {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import {
  canViewPanelArea,
  canWritePanelArea,
  type PanelPermission,
} from '../utils/panelPermissions';

export type PanelAccessValue = {
  permissions: string[] | null;
  isGodMode: boolean;
  /** True once permissions have been resolved for the session/org. */
  ready: boolean;
  canView: (area: string) => boolean;
  canWrite: (area: string) => boolean;
};

const PanelAccessContext = createContext<PanelAccessValue | null>(null);

export function PanelAccessProvider({
  permissions,
  isGodMode = false,
  children,
}: {
  permissions: string[] | null;
  isGodMode?: boolean;
  children: ReactNode;
}) {
  const value = useMemo<PanelAccessValue>(() => {
    const ready = isGodMode || permissions != null;
    return {
      permissions,
      isGodMode,
      ready,
      canView: (area: string) =>
        canViewPanelArea(permissions, area, isGodMode),
      canWrite: (area: string) =>
        canWritePanelArea(permissions, area, isGodMode),
    };
  }, [permissions, isGodMode]);

  return (
    <PanelAccessContext.Provider value={value}>
      {children}
    </PanelAccessContext.Provider>
  );
}

export function usePanelAccess(): PanelAccessValue {
  const ctx = useContext(PanelAccessContext);
  if (!ctx) {
    return {
      permissions: null,
      isGodMode: false,
      ready: false,
      canView: () => true,
      canWrite: () => true,
    };
  }
  return ctx;
}

/** Convenience: write access for a known area. */
export function useCanWritePanelArea(area: PanelPermission | string): boolean {
  const { canWrite } = usePanelAccess();
  return canWrite(area);
}
